import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { defaultsDeep, map, filter } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import { addCommasToNumber, UtilService } from "src/app/util/util.service";

enum MenuOption {
  ALLOWED = "Allowed",
  PREFERRED = "Preffered",
}

const DEFUALT_PIE_CHART_OPTIONS = {
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },
  tooltip: {
    trigger: "item",
    formatter: (params: any, ticket: any, callback: any) => {
      return `<strong>${params.name}</strong>
      <br/>Percent: ${params.percent}%
      <br/>Total: ${addCommasToNumber(params.value)}`;
    },
  },
  series: [
    {
      type: "pie",
      radius: ["43%", "90%"],
      label: {
        show: true,
        position: "inside",
      },

      emphasis: {
        label: {
          show: true,
          fontSize: "20",
          fontWeight: "bold",
        },
      },
      labelLine: {
        show: false,
      },
    },
  ],
};

const DEFUALT_HOR_BAR_CHART_OPTIONS = {
  grid: {
    left: 56,
    top: 0,
    right: 30,
    bottom: 50,
  },
  xAxis: {
    type: "value",
    position: "top",
    axisLabel: { show: false },

    splitLine: {
      show: false,
    },
  },
  yAxis: {
    show: true,
    type: "category",
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
  },
  series: [
    {
      itemStyle: { color: "#A1C700", borderRadius: [0, 5, 5, 0] },
      type: "bar",
      label: {
        show: true,
        position: "right",
        color: "#A1C700",
      },
    },
  ],
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

@Component({
  selector: "app-executive-summary",
  templateUrl: "./executive-summary.component.html",
  styleUrls: ["./executive-summary.component.scss"],
})
export class ExecutiveSummaryComponent implements OnInit {
  searchValue = "";

  mobileLocationOption = MenuOption.ALLOWED;
  mobilePushOption = MenuOption.ALLOWED;
  browserPushOption = MenuOption.ALLOWED;
  mobilePushChartOptions: any;
  totalInstallsChartOptions: any;
  userRegistrationsChartOptions: any;
  avgNumberOnPropVisitsChartOptions: any;
  mobileLocationChartOption: any;
  onPropVisitFreqOptions: any;
  totalInstalls: any;
  installsRange: any;
  installsType: any;
  filterForm!: FormGroup;
  registrationsType: any;
  registrationsRange: any;
  totalRegistrations: any;
  averageLocationVisits: any;
  locationAllowed: any;
  locationBreakdown: any;
  pushAllowed: any;
  onPropertyVisitFreq: any;
  propertyType: string = "off_property";
  propertyValueType: string = "visits";
  propertySortType: string = "high_to_low";
  offProperty: any;
  offPropertyVisitsTotal!: number;
  offPropertyAVGTotal: any;
  onProperty: any;
  onPropertyAVGTotal!: number;
  onPropertyVisitsTotal!: number;
  visitFrequencyType: string = "on_property";
  offPropertyVisitFreq: any;
  offPropVisitFreqOptions: any;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private AppService: AppService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private uiService: UiService,
    private util: UtilService,
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    this.initData(this.route.snapshot.data.executiveSummary);

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (this.filterForm.valid && change.startDate < change.endDate) {
          this.filterForm.controls.endDate.value.setHours(23, 59, 59);

          const facilityIDs = this.SessionService.facilitiesValue?.length
            ? this.SessionService.facilitiesValue.map((facility) => {
                return facility.id;
              })
            : [];

          this.uiService
            .waitForEvent(
              this.AppService.getExecutiveSummary(
                this.SessionService.currentAccountValue!.accountID,
                facilityIDs,
                change.startDate,
                this.filterForm.controls.endDate.value,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (executiveSummary) => {
                this.initData(executiveSummary);
              },
              (err) => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving information",
                );
              },
            );
        }
      });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get propertyPrefix() {
    if (this.propertyType === "on_property") {
      return "On Property";
    }
    return "Off Property";
  }

  get propertySuffix() {
    if (this.propertyValueType === "visits") {
      return "Visits";
    }
    return "Average TOS";
  }

  get visitFrequencyPrefix() {
    if (this.visitFrequencyType === "on_property") {
      return "On Property";
    }
    return "Off Property";
  }

  initData(executiveSummary: any) {
    this.totalInstalls = Number.parseInt(executiveSummary.total_installs ?? 0);

    this.installsRange = executiveSummary.installs.installs_range;

    this.installsType = executiveSummary.installs.type;

    this.totalRegistrations = executiveSummary.registered;

    this.registrationsType = executiveSummary.registrations.type;

    this.registrationsRange =
      executiveSummary.registrations.registrations_range;

    console.log(this.registrationsRange);

    this.locationAllowed = executiveSummary.location_allowed;

    this.locationBreakdown = executiveSummary.location_breakdown;

    this.pushAllowed = Number.parseInt(executiveSummary.push_allowed ?? 0);

    this.averageLocationVisits = executiveSummary.average_location_visits ?? 0;

    this.onPropertyVisitFreq = executiveSummary.on_property_visit_frequency;

    this.offPropertyVisitFreq = executiveSummary.off_property_visit_frequency;

    this.offProperty = executiveSummary.off_property;

    this.onProperty = executiveSummary.on_property;

    this.onPropertyVisitFreq.reverse();

    this.offPropertyVisitFreq.reverse();

    this.initTotalInstallsChart();

    this.initUserRegistrationsChart();

    // this.avgNumberOnPropVisitsChartOptions = defaultsDeep(
    //   {},
    //   DEFAULT_BAR_CHART_OPTIONS,
    //   {
    //     xAxis: {
    //       data: ["a", "b", "c", "d", "e", "f", "g"],
    //     },
    //     series: [
    //       {
    //         data: [120, 200, 150, 80, 70, 110, 130],
    //         label: {
    //           color: "#354c57",
    //         },
    //       },
    //     ],
    //   },
    // );

    this.mobileLocationChartOption = defaultsDeep(
      {
        grid: {
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
        },
        hover: { mode: null },

        series: [
          {
            name: "Mobile location permissions",
            data: map(
              filter(this.locationBreakdown, (r) => {
                return r.count > 0;
              }),
              (r) => {
                return this.util.formatPermissionBreakdown(r);
              },
            ),
            label: {
              show: false,
            },
            emphasis: {
              label: {
                show: false,
              },
            },
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    this.mobilePushChartOptions = defaultsDeep(
      {
        series: [
          {
            name: "Mobile push",
            data: [
              {
                value: this.totalInstalls - this.pushAllowed,

                name: "No",
                label: {
                  color: "#AAAAAA",
                },
                itemStyle: {
                  color: "#F0F0F0",
                },
                emphasis: {
                  itemStyle: {
                    color: "#EAEAEA",
                  },
                },
              },
            ],
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    if (this.pushAllowed) {
      this.mobilePushChartOptions.series[0].data.push({
        value: this.pushAllowed,
        name: "Yes",
        label: {
          color: "#FFF",
        },
        itemStyle: {
          color: "#7DC4FD",
        },
      });
    }

    this.onPropVisitFreqOptions = defaultsDeep(
      {
        yAxis: {
          data: this.onPropertyVisitFreq.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return label;
          }),
        },
        series: [
          {
            data: this.onPropertyVisitFreq.map((range: any) => {
              return range.count;
            }),
          },
        ],
      },
      DEFUALT_HOR_BAR_CHART_OPTIONS,
    );

    this.offPropVisitFreqOptions = defaultsDeep(
      {
        yAxis: {
          data: this.offPropertyVisitFreq.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return label;
          }),
        },
        series: [
          {
            data: this.offPropertyVisitFreq.map((range: any) => {
              return range.count;
            }),
          },
        ],
      },
      DEFUALT_HOR_BAR_CHART_OPTIONS,
    );

    this.offPropertyVisitsTotal = this.getPropertyVisitsTotal(this.offProperty);

    this.offPropertyAVGTotal = this.getPropertyAVGTOSTotal(this.offProperty);

    this.onPropertyVisitsTotal = this.getPropertyVisitsTotal(this.onProperty);

    this.onPropertyAVGTotal = this.getPropertyAVGTOSTotal(this.onProperty);

    this.offProperty = this.offProperty.map((property: any) => {
      const total_avg_mins =
        (property.avg_tos.hours ?? 0) * 60 + (property.avg_tos.minutes ?? 0);

      return {
        ...property,
        visits_num: Number.parseInt(property.visits ?? 0),
        total_avg_mins,
      };
    });

    this.onProperty = this.onProperty.map((property: any) => {
      const total_avg_mins =
        (property.avg_tos.hours ?? 0) * 60 + (property.avg_tos.minutes ?? 0);

      return {
        ...property,
        visits_num: Number.parseInt(property.visits ?? 0),
        total_avg_mins,
      };
    });

    this.sortProperies();
  }

  onPropertyChange() {}

  getPropertyVisitsTotal(properties: any[]) {
    let total = 0;
    properties.forEach((property: any) => {
      total = total + Number.parseInt(property.visits ?? 0);
    });

    return total;
  }

  getPropertyAVGTOSTotal(properties: any[]) {
    let total = 0;
    properties.forEach((property: any) => {
      const minutes =
        (property.avg_tos.hours ?? 0) * 60 + (property.avg_tos.minutes ?? 0);
      total = total + minutes;
    });

    return total;
  }

  sortProperies() {
    if (this.propertySortType === "high_to_low") {
      this.offProperty.sort((a: any, b: any) => {
        if (this.propertyValueType === "visits") {
          return b.visits_num - a.visits_num;
        } else {
          return b.total_avg_mins - a.total_avg_mins;
        }
      });
      this.onProperty.sort((a: any, b: any) => {
        if (this.propertyValueType === "visits") {
          return b.visits_num - a.visits_num;
        } else {
          return b.total_avg_mins - a.total_avg_mins;
        }
      });
    } else {
      this.offProperty.sort((a: any, b: any) => {
        if (this.propertyValueType === "visits") {
          return a.visits_num - b.visits_num;
        } else {
          return a.total_avg_mins - b.total_avg_mins;
        }
      });
      this.onProperty.sort((a: any, b: any) => {
        if (this.propertyValueType === "visits") {
          return b.visits_num - a.visits_num;
        } else {
          return a.total_avg_mins - b.total_avg_mins;
        }
      });
    }
  }

  initTotalInstallsChart() {
    let type: "Day" | "Week" | "Month";

    if (this.installsRange.length < 90) {
      type = "Day";
    } else if (this.installsRange.length < 180) {
      type = "Week";
    } else {
      type = "Month";
    }

    const installDataObj: {
      type: "Day" | "Week" | "Month";
      data: {
        date: Date;
        total: number;
      }[];
    } = { type, data: [] };

    if (type === "Day") {
      for (let dateRange of this.installsRange) {
        installDataObj.data.push({
          date: dateRange.install_date,
          total: dateRange.installs,
        });
      }
    } else if (type === "Week") {
      this.installsRange.sort((a: any, b: any) => {
        return (a.install_date as string).localeCompare(b.install_date);
      });

      const minDate: Date = new Date(this.installsRange[0].install_date);
      const maxDate = new Date(
        this.installsRange[this.installsRange.length - 1].install_date,
      );

      let currStartWeek = minDate;
      while (currStartWeek.getTime() < maxDate.getTime()) {
        const weekEnd = new Date(currStartWeek);

        weekEnd.setDate(currStartWeek.getDate() + 7);

        const weekObj = {
          weekStart: currStartWeek,
          weekEnd,
          total: 0,
        };

        const installsInRange = this.installsRange.filter((install: any) => {
          const date = new Date(install.install_date);
          return (
            date.getTime() >= weekObj.weekStart.getTime() &&
            date.getTime() < weekObj.weekEnd.getTime()
          );
        });

        installsInRange.forEach((install: any) => {
          weekObj.total = weekObj.total + install.installs;
        });

        installDataObj.data.push({
          date: weekObj.weekStart,
          total: weekObj.total,
        });

        currStartWeek = weekEnd;
      }
    } else if (type === "Month") {
      this.installsRange.sort((a: any, b: any) => {
        return (a.install_date as string).localeCompare(b.install_date);
      });

      const minDate = new Date(this.installsRange[0].install_date);

      const maxDate = new Date(
        this.installsRange[this.installsRange.length - 1].install_date,
      );

      let currMonth = minDate;

      while (currMonth.getTime() < maxDate.getTime()) {
        const monthEnd = new Date(currMonth);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(1);
        monthEnd.setHours(0);
        monthEnd.setMinutes(0);
        monthEnd.setSeconds(0);
        monthEnd.setMilliseconds(0);

        const monthObj = {
          month: currMonth,
          total: 0,
        };

        const installsInRange = this.installsRange.filter((install: any) => {
          const date = new Date(install.install_date);
          return (
            date.getTime() >= monthObj.month.getTime() &&
            date.getTime() < monthEnd.getTime()
          );
        });

        installsInRange.forEach((install: any) => {
          monthObj.total = monthObj.total + install.installs;
        });

        installDataObj.data.push({
          date: monthObj.month,
          total: monthObj.total,
        });

        currMonth = monthEnd;
      }
    }

    this.totalInstallsChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 22,
      },

      xAxis: {
        type: "category",
        boundaryGap: false,

        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          return this.getTooltip(params, type, params[0].axisValue);
        },
      },
      series: [
        {
          data: installDataObj.data.map((dateInstall: any) => {
            return [dateInstall.date, dateInstall.total];
          }),
          showSymbol: false,

          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
          barMaxWidth: "30%",
        },
      ],
    };
  }

  initUserRegistrationsChart() {
    let type: "Day" | "Week" | "Month";

    console.log(this.registrationsRange.length);

    if (this.registrationsRange.length < 90) {
      type = "Day";
    } else if (this.registrationsRange.length < 180) {
      type = "Week";
    } else {
      type = "Month";
    }

    const registrationsDataObj: {
      type: "Day" | "Week" | "Month";
      data: {
        date: Date;
        total: number;
      }[];
    } = { type, data: [] };

    if (type === "Day") {
      for (let dateRange of this.registrationsRange) {
        registrationsDataObj.data.push({
          date: dateRange.registration_date,
          total: dateRange.registrations,
        });
      }
    } else if (type === "Week") {
      this.registrationsRange.sort((a: any, b: any) => {
        return (a.registration_date as string).localeCompare(
          b.registration_date,
        );
      });

      const minDate: Date = new Date(
        this.registrationsRange[0].registration_date,
      );
      const maxDate = new Date(
        this.registrationsRange[
          this.registrationsRange.length - 1
        ].registration_date,
      );

      let currStartWeek = minDate;
      while (currStartWeek.getTime() < maxDate.getTime()) {
        const weekEnd = new Date(currStartWeek);

        weekEnd.setDate(currStartWeek.getDate() + 7);

        const weekObj = {
          weekStart: currStartWeek,
          weekEnd,
          total: 0,
        };

        const registrationsInRange = this.registrationsRange.filter(
          (registration: any) => {
            const date = new Date(registration.registration_date);
            return (
              date.getTime() >= weekObj.weekStart.getTime() &&
              date.getTime() < weekObj.weekEnd.getTime()
            );
          },
        );

        registrationsInRange.forEach((registration: any) => {
          weekObj.total = weekObj.total + registration.registrations;
        });

        registrationsDataObj.data.push({
          date: weekObj.weekStart,
          total: weekObj.total,
        });

        currStartWeek = weekEnd;
      }
    } else if (type === "Month") {
      this.registrationsRange.sort((a: any, b: any) => {
        return (a.registration_date as string).localeCompare(
          b.registration_date,
        );
      });

      const minDate = new Date(this.registrationsRange[0].registration_date);

      const maxDate = new Date(
        this.registrationsRange[
          this.registrationsRange.length - 1
        ].registration_date,
      );

      let currMonth = minDate;

      while (currMonth.getTime() < maxDate.getTime()) {
        const monthEnd = new Date(currMonth);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(1);
        monthEnd.setHours(0);
        monthEnd.setMinutes(0);
        monthEnd.setSeconds(0);
        monthEnd.setMilliseconds(0);

        const monthObj = {
          month: currMonth,
          total: 0,
        };

        const registrationsInRange = this.registrationsRange.filter(
          (registration: any) => {
            const date = new Date(registration.registration_date);
            return (
              date.getTime() >= monthObj.month.getTime() &&
              date.getTime() < monthEnd.getTime()
            );
          },
        );

        registrationsInRange.forEach((registration: any) => {
          monthObj.total = monthObj.total + registration.registrations;
        });

        registrationsDataObj.data.push({
          date: monthObj.month,
          total: monthObj.total,
        });

        currMonth = monthEnd;
      }
    }
    console.log(registrationsDataObj.data);

    this.userRegistrationsChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 22,
      },

      xAxis: {
        type: "category",
        boundaryGap: false,

        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          return this.getTooltip(params, type, params[0].axisValue);
        },
      },
      series: [
        {
          data: registrationsDataObj.data.map((dateRegistration: any) => {
            return [dateRegistration.date, dateRegistration.total];
          }),
          showSymbol: false,

          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
          barMaxWidth: "30%",
        },
      ],
    };
  }

  getXLabel(dateString: string, type: "Day" | "Week" | "Month") {
    const date = new Date(dateString);
    if (type === "Day") {
      return this.getDayLabel(date);
    } else if (type === "Week") {
      return this.getWeekLabel(date);
    } else if (type === "Month") {
      return this.getMonthLabel(date);
    }

    return "";
  }

  getDayLabel(date: Date) {
    if (date.getDate() === 1) {
      return MONTHS[date.getMonth()];
    }

    return date.getDate().toString();
  }

  getWeekLabel(date: Date) {
    const currentDate = date.getDate();
    if (currentDate < 7) {
      return MONTHS[date.getMonth()];
    }
    return `${date.getMonth()}/${date.getDate()}`;
  }

  getMonthLabel(date: Date) {
    const currentMonth = date.getMonth();
    if (currentMonth === 0) {
      return date.getFullYear().toString();
    }
    return MONTHS[currentMonth];
  }

  getTooltip(params: any, type: "Day" | "Week" | "Month", dateString: string) {
    const date = new Date(dateString);

    if (type === "Day") {
      return `
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "Week") {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7);
      return `
      Week Of:<br>
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${
        MONTHS[endDate.getMonth()]
      } ${endDate.getDate()} ${endDate.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "Month") {
      return `
      ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}
      <br><strong>${params[0].value[1]}</strong>
      `;
    }
    return "";
  }
}
